/**
 * Order settings — order-flow behaviour an administrator chooses here rather than in a config file
 * (owner request, 2026-10-14). Built on the same template pieces as `DeliveryZones.tsx`'s charge card:
 *
 * vendor/isomorphic/apps/isomorphic/src/app/shared/account-settings/personal-info.tsx
 *   the settings-screen shape: one card per setting, its explanation beside its control;
 * vendor/isomorphic/apps/isomorphic/src/app/shared/profile/profile-header.tsx
 *   the hero, through `components/detail-parts.tsx`.
 *
 * First setting: short fulfilment (FR-056, D-15) — whether an order Warehouse packs short waits for
 * an administrator to settle it ("Settle short fulfilment…" on the order), or the automatic choice
 * applies at once. `/admin/fulfilment-settings`; until someone saves, configuration
 * (`Ordering:ShortFulfilmentDecisionRequired`/`ShortFulfilmentDefault`) decides, and the card says so.
 * Gated on `orders.administer` by `nav.tsx`'s entry through the root loader — the permission that
 * settles a short order (owner decision, 2026-10-14).
 */
import { useState, type FormEvent } from "react";
import toast from "react-hot-toast";
import { useLoaderData, useRevalidator } from "react-router-dom";
import { PiGearSixDuotone as GearIcon, PiPackageDuotone as PackageIcon, PiSlidersHorizontalDuotone as SourceIcon } from "react-icons/pi";
import { Badge, Button, Text } from "rizzui";
import { ErrorSurface, WidgetCard } from "@steelyard/theme";
import { CheckboxCard } from "../../components/checkbox-card";
import { CardTitle, PageHero } from "../../components/detail-parts";
import { SelectField } from "../../components/select-field";
import {
  getFulfilmentSettings,
  setFulfilmentSettings,
  type FulfilmentSettings,
  type ShortFulfilmentChoice,
} from "../../lib/services/ordering";

export async function orderSettingsLoader() {
  return { fulfilment: (await getFulfilmentSettings()) as FulfilmentSettings };
}

const CHOICE_LABEL: Record<ShortFulfilmentChoice, string> = {
  ship_short: "Ship short — invoice what was packed",
  hold: "Hold — keep the order held, raise no invoice",
};

/** FR-056: edited here; what is in force is re-read from the API after a save. */
function ShortFulfilmentCard({ initial }: { initial: FulfilmentSettings }) {
  const revalidator = useRevalidator();
  const [decisionRequired, setDecisionRequired] = useState(initial.decisionRequired);
  const [automatic, setAutomatic] = useState(initial.automaticDecision as ShortFulfilmentChoice);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function save(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await setFulfilmentSettings({ decisionRequired, automaticDecision: automatic });
      toast.success("Short fulfilment setting saved");
      revalidator.revalidate();
    } catch (err) {
      setError(err);
      toast.error("Could not save the short fulfilment setting");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={save} className="mt-5 flex flex-col gap-4">
      <Text className="text-sm text-gray-500">
        When the warehouse packs less than an order asked for. Applies to the next order packed short; an order already
        decided keeps its decision.
        {initial.source === "database" ? " Saved here." : " Currently from configuration — saving here takes over."}
      </Text>
      {error ? <ErrorSurface error={error} action="save the short fulfilment setting" /> : null}
      <CheckboxCard
        label="Ask an admin to decide when an order is packed short"
        helperText={
          decisionRequired
            ? "On: the order waits, admins are notified, and the order offers “Settle short fulfilment…” to ship short or hold."
            : "Off: no one is asked — the choice below is applied as soon as the warehouse reports the short pack."
        }
        checked={decisionRequired}
        onChange={(event) => setDecisionRequired(event.target.checked)}
      />
      {decisionRequired ? null : (
        <SelectField
          label="When no one is asked"
          name="automaticDecision"
          value={automatic}
          onChange={(event) => setAutomatic(event.target.value as ShortFulfilmentChoice)}
          hint="Ship short refunds the difference on a prepaid order; a credit order is simply invoiced for what shipped."
        >
          {(Object.keys(CHOICE_LABEL) as ShortFulfilmentChoice[]).map((key) => (
            <option key={key} value={key}>
              {CHOICE_LABEL[key]}
            </option>
          ))}
        </SelectField>
      )}
      <div>
        <Button type="submit" disabled={busy}>
          {busy ? "Saving…" : "Save setting"}
        </Button>
      </div>
    </form>
  );
}

export default function OrderSettingsPage() {
  const { fulfilment } = useLoaderData() as Awaited<ReturnType<typeof orderSettingsLoader>>;

  return (
    <div className="@container flex flex-col gap-6">
      <PageHero
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Orders", href: "/orders" }, { name: "Order Settings" }]}
        back={{ href: "/orders", label: "All orders" }}
        icon={<GearIcon aria-hidden className="size-9" />}
        iconClassName="bg-blue"
        title="Order Settings"
        description="How orders behave after they are placed. Each change applies to later orders only."
      />

      <WidgetCard
        rounded="xl"
        className="max-w-3xl"
        title={
          <CardTitle icon={<PackageIcon aria-hidden className="size-5" />} tile="bg-orange-lighter text-orange-dark">
            Short fulfilment
          </CardTitle>
        }
        action={
          <Badge variant="flat" rounded="pill" className="gap-1.5 whitespace-nowrap">
            <SourceIcon aria-hidden className="size-3.5" />
            {fulfilment.source === "database" ? "Saved here" : "From configuration"}
          </Badge>
        }
        headerClassName="items-center"
      >
        {/* Keyed on what the API returned, so a save's re-read resets the form to the behaviour in force. */}
        <ShortFulfilmentCard key={JSON.stringify(fulfilment)} initial={fulfilment} />
      </WidgetCard>
    </div>
  );
}
